import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { setDebugPrincipal } from './api';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  type: 'platform_member' | 'tenant_member';
  tenantId?: string;
  permissions: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean; // true during initial session check
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void> | void;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = 'nv_auth_user';
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount: try session bootstrap first, then fall back to localStorage
  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      // Try API session bootstrap (production path: Cloudflare Access provides JWT
      // or nv_session cookie from a previous email/password login).
      try {
        const res = await fetch(`${API_BASE}/v1/admin/me`, {
          headers: { 'Accept': 'application/json' },
          credentials: 'include', // Include Cloudflare Access / nv_session cookies
        });

        if (res.ok) {
          const json = await res.json() as {
            data: { id: string; type: string; email: string | null; tenantId: string | null; permissions?: string[] };
          };
          const data = json.data;
          if (!cancelled) {
            const apiUser: AuthUser = {
              id: data.id,
              email: data.email ?? 'unknown',
              displayName: data.email?.split('@')[0] ?? 'User',
              type: data.type as 'platform_member' | 'tenant_member',
              tenantId: data.tenantId ?? undefined,
              permissions: data.permissions ?? [],
            };
            setUser(apiUser);
            setLoading(false);
          }
          return;
        }
      } catch {
        // API not available or not authenticated -- fall through to localStorage
      }

      // Fall back to localStorage (development mode only)
      if (!cancelled) {
        if (import.meta.env.DEV) {
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
              const parsed = JSON.parse(stored) as AuthUser;
              if (!parsed.permissions) {
                parsed.permissions = ['*'];
              }
              setUser(parsed);
              setDebugPrincipal(parsed);
            }
          } catch {
            // ignore malformed data
          }
        }
        setLoading(false);
      }
    }

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    // Real backend login: POST /v1/admin/auth/login
    // On success the server sets an HttpOnly nv_session cookie; we then fetch
    // /v1/admin/me to hydrate the full user + permissions.
    const res = await fetch(`${API_BASE}/v1/admin/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      // Parse error code for better user messaging. Known codes that
      // warrant a specific user-facing message are thrown; generic
      // failures (including invalid_credentials) return false so the
      // LoginPage shows the neutral "Invalid email or password" string.
      let code: string | undefined;
      try {
        const json = await res.json() as { error?: { code: string; message: string } };
        code = json.error?.code;
      } catch {
        // Body was not JSON; fall through with code=undefined.
      }

      if (code === 'password_not_set') {
        throw new Error('Password not yet set for this account. Contact your administrator.');
      }
      if (code === 'rate_limited') {
        throw new Error('Too many login attempts. Please wait and try again.');
      }
      if (code === 'account_disabled') {
        throw new Error('Account is disabled.');
      }
      if (code === 'account_locked') {
        throw new Error('Account temporarily locked after too many failed attempts.');
      }
      // invalid_credentials, invalid_request, or unknown: return false so the
      // form shows the generic "Invalid email or password" message.
      return false;
    }

    // Success -- server set the nv_session cookie via Set-Cookie header.
    const json = await res.json() as {
      data: { id: string; email: string; type: string; tenantId?: string; permissions?: string[] };
    };
    const data = json.data;

    // Now fetch /v1/admin/me to pick up resolved permissions from role_bindings.
    try {
      const meRes = await fetch(`${API_BASE}/v1/admin/me`, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });

      if (meRes.ok) {
        const meJson = await meRes.json() as {
          data: { id: string; email: string | null; type: string; tenantId: string | null; permissions?: string[] };
        };
        const me = meJson.data;
        const apiUser: AuthUser = {
          id: me.id,
          email: me.email ?? data.email,
          displayName: (me.email ?? data.email).split('@')[0] || 'User',
          type: me.type as 'platform_member' | 'tenant_member',
          tenantId: me.tenantId ?? undefined,
          permissions: me.permissions ?? [],
        };
        setUser(apiUser);
        return true;
      }
    } catch {
      // /me failed -- fall through to minimal user below.
    }

    // /me failed but login succeeded -- unusual, but still report success
    // and set a minimal user object from the login response.
    const apiUser: AuthUser = {
      id: data.id,
      email: data.email,
      displayName: data.email.split('@')[0] || 'User',
      type: data.type as 'platform_member' | 'tenant_member',
      tenantId: data.tenantId,
      permissions: data.permissions ?? [],
    };
    setUser(apiUser);
    return true;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server-side session revocation. We always clear client
    // state regardless of whether the network call succeeds.
    try {
      await fetch(`${API_BASE}/v1/admin/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort; clear client state regardless.
    }
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    setDebugPrincipal(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: user !== null, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
